const {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} = require('react');

const { push: Push } = require('connected-react-router');
const { useDispatch } = require('react-redux');
const { default: Styled } = require('styled-components');
const { default: Alert } = require('@mui/material/Alert');
const { default: Button } = require('@mui/material/Button');
const { default: CircularProgress } = require('@mui/material/CircularProgress');
const { default: Paper } = require('@mui/material/Paper');
const { default: Typography } = require('@mui/material/Typography');
const { context } = require('app-context');
const { useAppSelector } = require('utils/redux');
const useForm = require('hooks/useForm');
const VerifyMfaFormConfig = require('./formConfig');
const RoutingTable = require('services/auth-management/routing-table');
const AuthConstants = require('services/auth-management/constants');
const MfaCooldown = require('services/auth-management/mfa-cooldown');

const selectors = context.selectors.all;
const FORM_ID = 'verify-mfa-form';
const DEFAULT_MFA_COOLDOWN_SECONDS = MfaCooldown.DEFAULT_MFA_COOLDOWN_SECONDS;
const RESEND_COOLDOWN_SECONDS = 2 * 60;
const MFA_ERROR_MESSAGES = {
    MFA_CHALLENGE_EXPIRED: 'This verification session has expired.',
    MFA_CHALLENGE_FORBIDDEN: 'This verification session is no longer available.',
    MFA_CHALLENGE_NOT_FOUND: 'This verification session is no longer available.',
    MFA_CHALLENGE_USED: 'This verification session has already been used.',
    MFA_INVALID_CODE: 'That verification code is incorrect. Check it and try again.',
    MFA_METHOD_UNSUPPORTED: 'This verification method is not supported.',
    MFA_PHONE_CHANGE_FORBIDDEN: 'This verification session cannot change your phone number. Return to the previous step to continue securely.',
    MFA_PHONE_INVALID: 'Enter a valid mobile phone number.',
    MFA_PHONE_REQUIRED: 'A mobile phone number is required.'
};
const INVALID_MFA_REQUEST_MESSAGE = 'Invalid request payload input';
const INVALID_MFA_SESSION_MESSAGE = 'We couldn\'t request a new verification code. Go to the login page and sign in again to restart verification.';
const MFA_RATE_LIMIT_MESSAGES = {
    MFA_CHECK_RATE_LIMITED: 'Too many verification attempts.',
    MFA_MAX_ATTEMPTS: 'Too many incorrect attempts. This verification can no longer be used.',
    MFA_SEND_RATE_LIMITED: 'No new code was sent. Use the most recent code or wait before requesting another.',
    MFA_VERIFY_THROTTLED: 'Verification is temporarily busy.'
};
const MFA_COOLDOWN_CODES = new Set([
    'MFA_CHECK_RATE_LIMITED',
    'MFA_MAX_ATTEMPTS',
    'MFA_SEND_RATE_LIMITED'
]);
const MFA_VERIFICATION_LOCK_CODES = new Set([
    'MFA_CHECK_RATE_LIMITED',
    'MFA_MAX_ATTEMPTS'
]);
const MFA_TERMINAL_CHALLENGE_CODES = new Set([
    'MFA_CHALLENGE_EXPIRED',
    'MFA_CHALLENGE_FORBIDDEN',
    'MFA_CHALLENGE_NOT_FOUND',
    'MFA_CHALLENGE_USED'
]);
const internals = {};

module.exports = function VerifyMfa() {

    const dispatch = useDispatch();
    const loginContext = useAppSelector(selectors.getLoginContext) || {};
    const signupContext = useAppSelector(selectors.getSignupContext) || {};
    const storedMfaError = useAppSelector(selectors.getMfaError);
    const isProfilePhoneUpdate = internals.isProfilePhoneUpdate(loginContext);
    const mfaContext = isProfilePhoneUpdate || !signupContext.mfaToken
        ? loginContext
        : signupContext;
    const isSignupMfaContext = !isProfilePhoneUpdate && !!signupContext.mfaToken;
    const [errorMessage, setErrorMessage] = useState('');
    const [isCompleting, setIsCompleting] = useState(false);
    const [isChangingPhone, setIsChangingPhone] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [cooldownNow, setCooldownNow] = useState(() => Date.now());
    const fallbackErrorCooldownUntil = useMemo(
        () => MfaCooldown.getCooldownUntil(storedMfaError),
        [storedMfaError]
    );
    const mfaError = internals.getMfaErrorForContext({
        context: mfaContext,
        error: storedMfaError,
        fallbackErrorCooldownUntil,
        now: cooldownNow
    });
    const cooldownUntil = internals.getCooldownUntil({
        context: mfaContext,
        error: mfaError,
        fallbackErrorCooldownUntil
    });
    const cooldownRecoveryHandled = useRef(false);
    const [resendCooldownSeconds, setResendCooldownSeconds] = useState(() => {

        if (!mfaContext.mfaToken || mfaContext.mfaNeedsPhone) {
            return 0;
        }

        return internals.getResendCooldownSeconds(mfaError);
    });
    const [sendConfirmation, setSendConfirmation] = useState(
        mfaContext.mfaToken && !mfaContext.mfaNeedsPhone
            ? 'Verification code sent. SMS delivery can take up to 2 minutes.'
            : ''
    );

    const needsPhone = !!mfaContext.mfaNeedsPhone;
    const mfaMethod = mfaContext.mfaMethod;
    const unsupportedMfaMethod = !!mfaMethod && !RoutingTable.isSupportedMfaMethod(mfaMethod);
    const apiErrorMessage = internals.getMfaErrorMessage(mfaError);
    const remainingCooldownSeconds = internals.getRemainingCooldownSeconds({
        cooldownUntil,
        now: cooldownNow
    });
    const hasProviderCooldown = Number.isFinite(cooldownUntil);
    const hasSetupCooldown = needsPhone && hasProviderCooldown;
    const isCooldownMode = MFA_COOLDOWN_CODES.has(mfaError?.code) || hasProviderCooldown;
    const isSendCooldown = mfaError?.code === 'MFA_SEND_RATE_LIMITED';
    const isSetupSendCooldown = needsPhone && (isSendCooldown || hasSetupCooldown);
    const verificationIsLocked = MFA_VERIFICATION_LOCK_CODES.has(mfaError?.code);
    const challengeIsTerminal = MFA_TERMINAL_CHALLENGE_CODES.has(mfaError?.code);
    const challengeHasExpired = mfaError?.code === 'MFA_CHALLENGE_EXPIRED';
    const submissionIsLocked = challengeIsTerminal
        || verificationIsLocked
        || isSetupSendCooldown;
    const canChangePhone = !isProfilePhoneUpdate
        && mfaContext.canChangePhone === true;
    const recoveryDestination = RoutingTable.getMfaRecoveryDestination({
        loginContext,
        signupContext
    });
    const recoveryLabel = internals.getRecoveryLabel({
        loginContext,
        signupContext
    });
    const sendRateLimitCooldownSeconds = !needsPhone && mfaError?.code === 'MFA_SEND_RATE_LIMITED'
        ? internals.getResendCooldownSeconds(mfaError)
        : null;
    const isResendCoolingDown = resendCooldownSeconds > 0;

    useEffect(() => {

        if (sendRateLimitCooldownSeconds === null) {
            return;
        }

        setResendCooldownSeconds((current) => Math.max(current, sendRateLimitCooldownSeconds));
    }, [sendRateLimitCooldownSeconds]);

    useEffect(() => {

        if (!isResendCoolingDown) {
            return undefined;
        }

        const intervalId = setInterval(() => {

            setResendCooldownSeconds((seconds) => Math.max(0, seconds - 1));
        }, 1_000);

        return () => clearInterval(intervalId);
    }, [isResendCoolingDown]);

    const clearMfaContext = useCallback(() => {

        const update = {
            authMethod: null,
            mfaToken: null,
            mfaMethod: null,
            mfaPurpose: null,
            mfaNeedsPhone: null,
            mfaPhone: null,
            mfaPhoneCountry: null,
            mfaCooldownCode: null,
            mfaCooldownUntil: null,
            maskedDestination: null,
            canChangePhone: null,
            authFlowSource: null,
            redirect: null,
            redirectTarget: null
        };

        dispatch(isSignupMfaContext
            ? context.actions.signup.updateContext({
                ...update,
                pendingPreferences: null
            })
            : context.actions.auth.updateLoginContext(update));
    }, [dispatch, isSignupMfaContext]);

    const submit = useCallback(async (values) => {

        setErrorMessage('');
        setSendConfirmation('');

        if (needsPhone) {
            let action;

            try {
                action = await dispatch(context.actions.auth.startMfaSetup({
                    mfaToken: mfaContext.mfaToken,
                    phone: values.phone,
                    phoneCountry: 'us'
                }));
            }
            catch (err) {
                action = undefined;
            }

            if (!action) {
                return;
            }

            setSendConfirmation('Verification code sent. SMS delivery can take up to 2 minutes.');
            setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
            return;
        }

        try {
            setIsCompleting(true);

            const action = await dispatch(context.actions.auth.verifyMfa({
                mfaToken: mfaContext.mfaToken,
                code: values.code.trim()
            }));

            if (!action?.payload?.result) {
                setIsCompleting(false);
            }
        }
        catch (err) {
            setIsCompleting(false);
            setErrorMessage('Unable to verify the code.');
        }
    }, [
        dispatch,
        mfaContext.mfaToken,
        needsPhone
    ]);

    const defaults = useMemo(() => VerifyMfaFormConfig.buildDefaults({
        code: '',
        needsPhone,
        phone: mfaContext.mfaPhone || ''
    }), [mfaContext.mfaPhone, needsPhone]);

    const formConfig = useMemo(() => VerifyMfaFormConfig.buildFormConfig({
        defaults,
        formId: FORM_ID,
        needsPhone,
        onSubmit: submit
    }), [defaults, needsPhone, submit]);

    const { methods, Form } = useForm(formConfig);

    const { isSubmitting } = methods.formState;

    const {
        Actions,
        Card,
        Content,
        FieldContainer,
        LoadingContent,
        Page
    } = internals;

    const handleResend = useCallback(async () => {

        if (!mfaContext.mfaToken) {
            setErrorMessage('No pending verification challenge was found.');
            return;
        }

        if (resendCooldownSeconds > 0) {
            return;
        }

        setErrorMessage('');
        setSendConfirmation('');
        setIsResending(true);
        setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);

        let action;

        try {
            action = await dispatch(context.actions.auth.resendMfaChallenge({
                mfaToken: mfaContext.mfaToken
            }));
        }
        catch (err) {
            action = undefined;
        }

        if (!action) {
            setIsResending(false);
            return;
        }

        setSendConfirmation('A new verification code was sent. SMS delivery can take up to 2 minutes.');
        setIsResending(false);
    }, [dispatch, mfaContext.mfaToken, resendCooldownSeconds]);

    const handleChangePhone = useCallback(async () => {

        setErrorMessage('');
        setSendConfirmation('');
        setIsChangingPhone(true);

        try {
            const action = await dispatch(context.actions.auth.resetMfaPhone({
                mfaToken: mfaContext.mfaToken
            }));

            if (!action) {
                setErrorMessage('Unable to change the phone number. Try signing in again.');
            }
        }
        catch (err) {
            setErrorMessage('Unable to change the phone number. Try signing in again.');
        }
        finally {
            setIsChangingPhone(false);
        }
    }, [dispatch, mfaContext.mfaToken]);

    const recoverFromMfa = useCallback(() => {

        clearMfaContext();
        dispatch(Push(recoveryDestination));
    }, [clearMfaContext, dispatch, recoveryDestination]);

    useEffect(() => {

        if (!mfaError?.code
            || !Number.isFinite(cooldownUntil)
            || (mfaContext.mfaCooldownCode === mfaError.code
                && Number(mfaContext.mfaCooldownUntil) === cooldownUntil)) {
            return;
        }

        const update = {
            mfaCooldownCode: mfaError.code,
            mfaCooldownUntil: cooldownUntil
        };

        if (isSignupMfaContext) {
            dispatch(context.actions.signup.updateContext(update));
            return;
        }

        dispatch(context.actions.auth.updateLoginContext(update));
    }, [
        cooldownUntil,
        dispatch,
        isSignupMfaContext,
        mfaContext.mfaCooldownCode,
        mfaContext.mfaCooldownUntil
    ]);

    useEffect(() => {

        if (!hasProviderCooldown) {
            return undefined;
        }

        const timer = setInterval(() => {

            setCooldownNow(Date.now());
        }, 1_000);

        return () => clearInterval(timer);
    }, [hasProviderCooldown]);

    useEffect(() => {

        if (!hasProviderCooldown
            || remainingCooldownSeconds > 0
            || cooldownRecoveryHandled.current) {
            return;
        }

        cooldownRecoveryHandled.current = true;
        clearMfaContext();
        dispatch(context.actions.alerts.notification.push({
            message: 'The verification cooldown has ended. Restart verification to continue.'
        }));
        dispatch(Push(recoveryDestination));
    }, [
        clearMfaContext,
        dispatch,
        hasProviderCooldown,
        recoveryDestination,
        remainingCooldownSeconds
    ]);

    if (isCompleting) {
        return <Page>
            <Card elevation={3}>
                <LoadingContent>
                    <CircularProgress aria-label='Completing verification' size={36} />
                    <Typography component='h1' variant='h4'>Finishing verification</Typography>
                    <Typography color='text.secondary'>Completing your sign-in...</Typography>
                </LoadingContent>
            </Card>
        </Page>;
    }

    if (!mfaContext.mfaToken) {
        return <Page>
            <Card elevation={3}>
                <Content>
                    <Typography component='h1' variant='h4'>Verification required</Typography>
                    <Alert severity='warning'>No pending verification challenge was found.</Alert>
                    <Button onClick={recoverFromMfa} variant='contained'>{recoveryLabel}</Button>
                </Content>
            </Card>
        </Page>;
    }

    if (unsupportedMfaMethod) {
        return <Page>
            <Card elevation={3}>
                <Content>
                    <Typography component='h1' variant='h4'>Verification required</Typography>
                    <Alert severity='error'>This verification method is not supported yet.</Alert>
                    <Button onClick={recoverFromMfa} variant='contained'>{recoveryLabel}</Button>
                </Content>
            </Card>
        </Page>;
    }

    return <Page>
        <Card elevation={3}>
            <Content>
                <Typography component='h1' variant='h4'>{isSetupSendCooldown
                    ? 'Restart verification'
                    : (isCooldownMode
                        ? 'Verification paused'
                    : (challengeIsTerminal
                        ? (challengeHasExpired ? 'Verification expired' : 'Verification unavailable')
                        : 'Verify your phone'))}
                </Typography>
                <Typography color='text.secondary'>{internals.getVerificationInstructions({
                    challengeIsTerminal,
                    isCooldownMode,
                    isSendCooldown,
                    isSetupSendCooldown,
                    maskedDestination: mfaContext.maskedDestination,
                    needsPhone,
                    recoveryLabel,
                    retryAfterSeconds: remainingCooldownSeconds
                })}
                </Typography>
                {sendConfirmation && !isCooldownMode && !challengeIsTerminal
                    ? <Alert severity='success'>{sendConfirmation}</Alert>
                    : null}
                {isResending ? <Alert severity='info'>Requesting a new verification code...</Alert> : null}
                {!submissionIsLocked ? <FieldContainer>{Form}</FieldContainer> : null}
                {!isSetupSendCooldown && (errorMessage || apiErrorMessage)
                    ? <Alert severity='error'>{errorMessage || apiErrorMessage}</Alert>
                    : null}
                <Actions>
                    {!submissionIsLocked ? <Button
                        disabled={isSubmitting}
                        form={FORM_ID}
                        fullWidth
                        size='large'
                        type='submit'
                        variant={isCooldownMode ? 'outlined' : 'contained'}
                    >
                        {isSubmitting ? (needsPhone ? 'Sending...' : 'Verifying...') : (needsPhone ? 'Send code' : 'Verify')}
                    </Button> : null}
                    {!needsPhone && !verificationIsLocked && !challengeIsTerminal ? <Button
                        disabled={isSubmitting || isResending || isChangingPhone || isResendCoolingDown}
                        fullWidth
                        onClick={handleResend}
                        size='large'
                        type='button'
                        variant='outlined'
                    >
                        {isResending
                            ? 'Sending...'
                            : (isResendCoolingDown
                                ? `Resend in ${internals.formatResendCountdown(resendCooldownSeconds)}`
                                : 'Resend code')}
                    </Button> : null}
                    {!needsPhone && !verificationIsLocked && !isCooldownMode && !challengeIsTerminal && canChangePhone ? <Button
                        disabled={isSubmitting || isResending || isChangingPhone}
                        fullWidth
                        onClick={handleChangePhone}
                        size='small'
                        type='button'
                        variant='text'
                    >
                        {isChangingPhone ? 'Changing phone number...' : 'Change phone number'}
                    </Button> : null}
                    <Button
                        disabled={isSubmitting || isResending || isChangingPhone}
                        fullWidth
                        onClick={recoverFromMfa}
                        type='button'
                        variant={isCooldownMode || challengeIsTerminal ? 'contained' : 'text'}
                    >
                        {recoveryLabel}
                    </Button>
                </Actions>
            </Content>
        </Card>
    </Page>;
};

internals.getCooldownUntil = ({
    context = {},
    error,
    fallbackErrorCooldownUntil
} = {}) => {

    const persistedCooldownUntil = Number(context.mfaCooldownUntil);

    if (Number.isFinite(persistedCooldownUntil) && persistedCooldownUntil > 0) {
        return persistedCooldownUntil;
    }

    if (!MfaCooldown.isCooldownError(error)) {
        return null;
    }

    return Number(error.cooldownUntil) || fallbackErrorCooldownUntil || null;
};

internals.getMfaErrorForContext = ({
    context = {},
    error,
    fallbackErrorCooldownUntil,
    now = Date.now()
} = {}) => {

    if (error && MfaCooldown.matchesChallenge({
        error,
        mfaToken: context.mfaToken
    })) {
        if (!MfaCooldown.isCooldownError(error)) {
            return error;
        }

        const cooldownUntil = Number(error.cooldownUntil)
            || fallbackErrorCooldownUntil;

        return {
            ...error,
            cooldownUntil,
            retryAfterSeconds: MfaCooldown.getRemainingSeconds(cooldownUntil, now)
        };
    }

    const persistedCooldownUntil = Number(context.mfaCooldownUntil);
    const persistedCooldownCode = context.mfaCooldownCode
        || (context.mfaNeedsPhone && persistedCooldownUntil
            ? 'MFA_SEND_RATE_LIMITED'
            : null);

    if (!persistedCooldownCode || !persistedCooldownUntil) {
        return null;
    }

    return {
        code: persistedCooldownCode,
        cooldownUntil: persistedCooldownUntil,
        mfaToken: context.mfaToken,
        retryAfterSeconds: MfaCooldown.getRemainingSeconds(persistedCooldownUntil, now)
    };
};

internals.getRemainingCooldownSeconds = ({ cooldownUntil, now = Date.now() } = {}) => {

    return MfaCooldown.getRemainingSeconds(cooldownUntil, now);
};

internals.getVerificationInstructions = ({
    challengeIsTerminal,
    isCooldownMode,
    isSendCooldown,
    isSetupSendCooldown,
    maskedDestination,
    needsPhone,
    recoveryLabel,
    retryAfterSeconds
} = {}) => {

    if (challengeIsTerminal) {
        return `${recoveryLabel} to start again.`;
    }

    if (isSetupSendCooldown) {
        const seconds = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
            ? Math.ceil(retryAfterSeconds)
            : DEFAULT_MFA_COOLDOWN_SECONDS;

        return `Verification is temporarily unavailable due to a cooldown. Go back and restart the verification process. Try again in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}.`;
    }

    if (isSendCooldown && !needsPhone) {
        return `You can still use the most recent code for ${maskedDestination || 'your phone'}, or ${recoveryLabel.toLowerCase()}.`;
    }

    if (isCooldownMode) {
        return `${recoveryLabel} now and try verification again after the cooldown.`;
    }

    return needsPhone
        ? 'Enter the mobile number you want to use for SMS verification.'
        : `Enter the 6-digit code for ${maskedDestination || 'your phone'} when it arrives.`;
};

internals.getRecoveryLabel = ({ loginContext = {}, signupContext = {} } = {}) => {

    if (internals.isProfilePhoneUpdate(loginContext)) {
        return 'Return to profile';
    }

    const hasSignupChallenge = !!signupContext.mfaToken;
    const context = hasSignupChallenge ? signupContext : loginContext;

    if (hasSignupChallenge) {
        return 'Return to signup';
    }

    if (context.authFlowSource === AuthConstants.authFlow.sources.profilePhoneUpdate
        || context.mfaPurpose === AuthConstants.authFlow.sources.profilePhoneUpdate) {
        return 'Return to profile';
    }

    if (context.authFlowSource === AuthConstants.authFlow.sources.signupCompletionMfa
        || context.mfaPurpose === AuthConstants.authFlow.sources.signupCompletionMfa) {
        return 'Return to signup';
    }

    return 'Go to login page';
};

internals.isProfilePhoneUpdate = (context = {}) => context.authFlowSource === AuthConstants.authFlow.sources.profilePhoneUpdate
    || context.mfaPurpose === AuthConstants.authFlow.sources.profilePhoneUpdate;

internals.getMfaErrorMessage = (error) => {

    if (!error) {
        return '';
    }

    if (MFA_ERROR_MESSAGES[error.code]) {
        return MFA_ERROR_MESSAGES[error.code];
    }

    if (MFA_RATE_LIMIT_MESSAGES[error.code]) {
        const retryAfterSeconds = error.retryAfterSeconds
            || (['MFA_CHECK_RATE_LIMITED', 'MFA_MAX_ATTEMPTS', 'MFA_SEND_RATE_LIMITED'].includes(error.code)
                ? DEFAULT_MFA_COOLDOWN_SECONDS
                : null);
        const retryMessage = internals.getRetryMessage(retryAfterSeconds);

        return `${MFA_RATE_LIMIT_MESSAGES[error.code]} ${retryMessage}`;
    }

    if (error.message === INVALID_MFA_REQUEST_MESSAGE) {
        return INVALID_MFA_SESSION_MESSAGE;
    }

    return error.message || 'Unable to complete phone verification.';
};

internals.getResendCooldownSeconds = (error) => {

    if (error?.code === 'MFA_SEND_RATE_LIMITED') {
        if (Number.isFinite(error.retryAfterSeconds) && error.retryAfterSeconds > 0) {
            return Math.ceil(error.retryAfterSeconds);
        }

        return DEFAULT_MFA_COOLDOWN_SECONDS;
    }

    return RESEND_COOLDOWN_SECONDS;
};

internals.formatResendCountdown = (totalSeconds) => {

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

internals.getRetryMessage = (retryAfterSeconds) => {

    if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
        return 'Try again shortly.';
    }

    if (retryAfterSeconds < 60) {
        return `Try again in ${Math.ceil(retryAfterSeconds)} seconds.`;
    }

    const minutes = Math.ceil(retryAfterSeconds / 60);

    return `Try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
};

internals.Page = Styled.main`
    box-sizing: border-box;
    min-height: 100vh;
    padding: 32px 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.palette.emptyBackground};
`;

internals.Card = Styled(Paper)`
    box-sizing: border-box;
    width: 100%;
    max-width: 480px;
    padding: 32px;
`;

internals.Content = Styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

internals.LoadingContent = Styled(internals.Content)`
    align-items: center;
    text-align: center;
`;

internals.FieldContainer = Styled.div`
    width: 100%;
`;

internals.Actions = Styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;
